/**
 * Station tier classification based on dataSourceId.
 *
 * - Public: all stations except those with DTN_EU, MCC, or DTN_AU data sources
 * - Plus: stations with DTN_EU data source
 * - Internal: stations with MCC or DTN_AU data source
 *
 * Internal takes precedence over Plus (a station with both DTN_EU and MCC is Internal).
 */

export type StationTier = 'public' | 'plus' | 'internal';

const INTERNAL_SOURCES = new Set(['MCC', 'DTN_AU']);
const PLUS_SOURCES = new Set(['DTN_EU']);

/** Classify a station's tier from its devices' dataSourceId values. */
export function getStationTier(
  devices: Array<{ dataSourceId: string }>,
): StationTier {
  let hasPlus = false;

  for (const d of devices) {
    const id = d.dataSourceId;
    if (!id) continue;
    if (INTERNAL_SOURCES.has(id)) return 'internal';
    if (PLUS_SOURCES.has(id)) hasPlus = true;
  }

  return hasPlus ? 'plus' : 'public';
}

/** Fill colors per tier — used for map markers. */
export const TIER_COLORS: Record<StationTier, string> = {
  public: '#10B981',   // emerald green — open/public data
  plus: '#F59E0B',     // --color-warning (amber — premium/plus tier)
  internal: '#6366F1', // indigo — internal/proprietary
};

/** Stroke colors per tier (slightly darker shade of fill). */
export const TIER_STROKE_COLORS: Record<StationTier, string> = {
  public: '#059669',
  plus: '#D97706',
  internal: '#4F46E5',
};
